import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { RefreshCw } from 'lucide-react';
import { auth } from '@/lib/firebase';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://sales-api-768596480524.us-central1.run.app';

const DEFAULT_FROM = (() => {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return d.toISOString().split('T')[0];
})();
const DEFAULT_TO = new Date().toISOString().split('T')[0];

interface EmailAggregateMetrics {
  open_rate: number;
  click_rate: number;
  bounce_rate: number;
  unsubscribe_rate: number;
  total_sent: number;
  total_delivered: number;
  total_opened: number;
  total_clicked: number;
  total_bounced: number;
  total_unsubscribed: number;
}

interface EmailStepMetric {
  template: string;
  sent: number;
  opened: number;
  clicked: number;
  bounced: number;
  unsubscribed: number;
  open_rate: number;
  click_rate: number;
}

interface HourMetric {
  hour: number;
  open_count: number;
  unsubscribe_count: number;
}

interface DayMetric {
  day: string;
  open_count: number;
  unsubscribe_count: number;
}

interface EmailSequenceAnalytics {
  aggregate_metrics: EmailAggregateMetrics;
  sequence_step_metrics: EmailStepMetric[];
  time_of_day: HourMetric[];
  day_of_week: DayMetric[];
  date_from: string;
  date_to: string;
}

async function getAuthHeader(): Promise<Record<string, string>> {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  const token = await user.getIdToken();
  return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
}

const fmtRate = (n: number) => `${n.toFixed(1)}%`;

function DualBar({
  openCount,
  unsubCount,
  max,
  label,
}: {
  openCount: number;
  unsubCount: number;
  max: number;
  label: string;
}) {
  const openPct  = max > 0 ? Math.round((openCount  / max) * 100) : 0;
  const unsubPct = max > 0 ? Math.round((unsubCount / max) * 100) : 0;
  return (
    <div className="flex items-center gap-2">
      <span className="w-8 text-xs text-right text-muted-foreground">{label}</span>
      <div className="flex-1 space-y-0.5">
        <div className="h-2 bg-muted rounded overflow-hidden">
          <div className="h-full bg-green-500 rounded" style={{ width: `${openPct}%` }} />
        </div>
        <div className="h-2 bg-muted rounded overflow-hidden">
          <div className="h-full bg-red-500 rounded" style={{ width: `${unsubPct}%` }} />
        </div>
      </div>
      <span className="w-14 text-xs text-muted-foreground">{openCount}o {unsubCount}u</span>
    </div>
  );
}

export function EmailView() {
  const [searchParams] = useSearchParams();
  const dateFrom  = searchParams.get('from')      ?? DEFAULT_FROM;
  const dateTo    = searchParams.get('to')         ?? DEFAULT_TO;
  const highlight = searchParams.get('highlight')  ?? null;

  const [emailAnalytics, setEmailAnalytics] = useState<EmailSequenceAnalytics | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchEmailAnalytics = async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeader();
      const params = `?date_from=${dateFrom}&date_to=${dateTo}`;
      const res = await fetch(`${API_BASE_URL}/api/analytics/email-sequences${params}`, { headers });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(`Failed to load email analytics: ${err.detail ?? res.statusText}`);
      }
      const data = await res.json();
      setEmailAnalytics(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to fetch email analytics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchEmailAnalytics(); }, [dateFrom, dateTo]);

  useEffect(() => {
    if (!highlight || loading) return;
    const timer = setTimeout(() => {
      const el = document.getElementById(`section-${highlight}`);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('ring-2', 'ring-blue-500', 'ring-offset-2');
      setTimeout(() => el.classList.remove('ring-2', 'ring-blue-500', 'ring-offset-2'), 3000);
    }, 150);
    return () => clearTimeout(timer);
  }, [highlight, loading]);

  return (
    <div className="space-y-6">
      {error && (
        <Card className="border-yellow-500/50 bg-yellow-500/10">
          <CardContent className="py-3 text-sm text-yellow-600">{error}</CardContent>
        </Card>
      )}

      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={fetchEmailAnalytics} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Aggregate metrics cards */}
      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
      ) : emailAnalytics ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card id="section-open_rate" className="bg-green-50/50 dark:bg-green-950/30">
              <CardContent className="p-4">
                <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Open Rate</span>
                <p className="text-3xl font-bold leading-tight mt-1">{fmtRate(emailAnalytics.aggregate_metrics.open_rate)}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {emailAnalytics.aggregate_metrics.total_opened} opens / {emailAnalytics.aggregate_metrics.total_delivered} delivered
                </p>
              </CardContent>
            </Card>
            <Card className="bg-green-50/50 dark:bg-green-950/30">
              <CardContent className="p-4">
                <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Click Rate</span>
                <p className="text-3xl font-bold leading-tight mt-1">{fmtRate(emailAnalytics.aggregate_metrics.click_rate)}</p>
                <p className="text-xs text-muted-foreground mt-1">{emailAnalytics.aggregate_metrics.total_clicked} clicks</p>
              </CardContent>
            </Card>
            <Card className="bg-red-50/50 dark:bg-red-950/30">
              <CardContent className="p-4">
                <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Bounce Rate</span>
                <p className="text-3xl font-bold leading-tight mt-1">{fmtRate(emailAnalytics.aggregate_metrics.bounce_rate)}</p>
                <p className="text-xs text-muted-foreground mt-1">{emailAnalytics.aggregate_metrics.total_bounced} bounced</p>
              </CardContent>
            </Card>
            <Card className="bg-red-50/50 dark:bg-red-950/30">
              <CardContent className="p-4">
                <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Unsubscribe Rate</span>
                <p className="text-3xl font-bold leading-tight mt-1">{fmtRate(emailAnalytics.aggregate_metrics.unsubscribe_rate)}</p>
                <p className="text-xs text-muted-foreground mt-1">{emailAnalytics.aggregate_metrics.total_unsubscribed} unsubscribed</p>
              </CardContent>
            </Card>
          </div>

          {/* Per-step performance table */}
          {emailAnalytics.sequence_step_metrics.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Per-Step Performance</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-muted-foreground border-b">
                        <th className="pb-2 pr-4">Template</th>
                        <th className="pb-2 pr-4 text-right">Sent</th>
                        <th className="pb-2 pr-4 text-right">Opened</th>
                        <th className="pb-2 pr-4 text-right">Clicked</th>
                        <th className="pb-2 pr-4 text-right">Bounced</th>
                        <th className="pb-2 pr-4 text-right">Unsub</th>
                        <th className="pb-2 pr-4 text-right">Open %</th>
                        <th className="pb-2 text-right">Click %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {emailAnalytics.sequence_step_metrics.map(step => (
                        <tr key={step.template} className="border-b last:border-0">
                          <td className="py-2 pr-4 font-medium">
                            {step.template && step.template !== 'unknown'
                              ? step.template
                              : <span className="text-muted-foreground italic">(unnamed template)</span>}
                          </td>
                          <td className="py-2 pr-4 text-right">{step.sent}</td>
                          <td className="py-2 pr-4 text-right">{step.opened}</td>
                          <td className="py-2 pr-4 text-right">{step.clicked}</td>
                          <td className="py-2 pr-4 text-right">{step.bounced}</td>
                          <td className="py-2 pr-4 text-right">{step.unsubscribed}</td>
                          <td className="py-2 pr-4 text-right">{fmtRate(step.open_rate)}</td>
                          <td className="py-2 text-right">{fmtRate(step.click_rate)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Time-of-day and day-of-week charts */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Activity by Hour of Day</CardTitle>
                <div className="flex gap-3 text-xs text-muted-foreground mt-1">
                  <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded bg-green-500" /> Opens</span>
                  <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded bg-red-500" /> Unsubscribes</span>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-1">
                  {(() => {
                    const maxVal = Math.max(...emailAnalytics.time_of_day.map(h => h.open_count), 1);
                    return emailAnalytics.time_of_day.map(h => (
                      <DualBar
                        key={h.hour}
                        openCount={h.open_count}
                        unsubCount={h.unsubscribe_count}
                        max={maxVal}
                        label={`${h.hour}h`}
                      />
                    ));
                  })()}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Activity by Day of Week</CardTitle>
                <div className="flex gap-3 text-xs text-muted-foreground mt-1">
                  <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded bg-green-500" /> Opens</span>
                  <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded bg-red-500" /> Unsubscribes</span>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {(() => {
                    const maxVal = Math.max(...emailAnalytics.day_of_week.map(d => d.open_count), 1);
                    return emailAnalytics.day_of_week.map(d => (
                      <DualBar
                        key={d.day}
                        openCount={d.open_count}
                        unsubCount={d.unsubscribe_count}
                        max={maxVal}
                        label={d.day.slice(0, 3)}
                      />
                    ));
                  })()}
                </div>
              </CardContent>
            </Card>
          </div>
        </>
      ) : (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            No email sequence data available for the selected date range.
          </CardContent>
        </Card>
      )}
    </div>
  );
}
